import { ExpandLess, ExpandMore, Close } from '@mui/icons-material';
import { Alert, IconButton, Typography, Collapse } from '@mui/material';
import { Box, styled } from '@mui/system';
import { memo, useCallback, useState } from 'react';

const PmiAlert = styled(Alert)(() => ({
  alignItems: 'center',
  '& .MuiAlert-message': {
    width: '100%',
  },
}));

export enum MessageType {
  Info = 'info',
  Error = 'error',
  Warning = 'warning',
}

export interface Message {
  id: string | number;
  value: string;
}

interface SearchErrorsProps {
  disabled?: boolean;
  title: string;
  type: MessageType;
  messages: Array<Message>;
  visibility: boolean;
  onChangeVisibility: (open: boolean, type: MessageType) => void;
}

const SearchMessagesComponent = ({
  disabled,
  title,
  messages,
  type,
  visibility,
  onChangeVisibility,
}: SearchErrorsProps): React.ReactElement | null => {
  const [open, setOpen] = useState<boolean>(true);

  const handleClose = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onChangeVisibility(false, type);
    },
    [onChangeVisibility, type]
  );

  const handleMessageVisibility = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen((oldState) => !oldState);
    },
    [setOpen]
  );

  return visibility ? (
    <PmiAlert severity={type}>
      <div className="pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <div className="pmi-d-flex pmi-align-items-center pmi-justify-content-between pmi-w-100">
          <Box component="div" sx={{ textOverflow: 'ellipsis' }}>
            {title}
          </Box>
          <IconButton disabled={disabled} onClick={handleMessageVisibility}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        <div className="pmi-pl-4">
          <IconButton disabled={disabled} onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
      </div>
      <Collapse in={open} timeout="auto">
        <span
          style={{
            whiteSpace: 'pre-line',
          }}
        >
          {messages.map((message) => (
            <Typography key={`${message.id}_${message.value}`}>{message.value}</Typography>
          ))}
        </span>
      </Collapse>
    </PmiAlert>
  ) : null;
};

SearchMessagesComponent.displayName = 'Search Messages component';

const SearchMessages = memo(SearchMessagesComponent);

export default SearchMessages as typeof SearchMessagesComponent;
